import { useEffect, useRef, useState } from 'react';

/**
 * Transient new-item pulse for the operations-panel side-by-side queues
 * (control-panel spec §2). Both protected queues are always visible side-by-side,
 * so there's no hidden tab to blink — instead a queue's header gives a brief,
 * one-shot pulse when that queue updates, so an operator notices the change
 * without watching the rows.
 *
 * Driven by the monotonic signal counters from `useQueueSignals` (the same
 * "blink signal" the retired stacked-tabs fallback used): a rise in the counter
 * is one update tick. This returns a `pulseKey` that increments ONLY when the
 * counter rises (never on first mount), so a keyed element can replay a finite
 * CSS pulse on each tick by remounting — no infinite animation, no sticky state.
 */
export function useQueuePulse(count: number): number {
  const [pulseKey, setPulseKey] = useState(0);
  // null until the first count is seen so the initial render never pulses.
  const last = useRef<number | null>(null);

  useEffect(() => {
    if (last.current !== null && count > last.current) {
      setPulseKey((k) => k + 1);
    }
    last.current = count;
  }, [count]);

  return pulseKey;
}
